import { useLayoutEffect } from 'react';

// ── The React-18 ↔ Web Awesome boolean-attribute trap (read before adding wa-* UI) ──
//
// This repo is React 18 (CRA / react-scripts 5). react-dom 18 does NOT special-case
// custom elements (hyphenated tags like <wa-button>) for boolean props: a `false`
// boolean is serialized as the string attribute `disabled="false"` rather than being
// removed. Web Awesome elements are Lit-based, and Lit's default Boolean converter
// treats ANY attribute presence as `true`. Net effect:
//
//   disabled={true}      -> setAttribute('disabled','true')  -> Lit reads true  (ok by luck)
//   disabled={false}     -> setAttribute('disabled','false') -> Lit reads true  (WRONG — stuck on)
//   disabled={undefined} -> attribute absent                 -> Lit reads false (ok)
//
// So a wa-* control bound to a boolean *variable* is stuck in the disabled/on state
// whenever that variable is false. (React 19 fixes custom-element boolean serialization;
// we are not on 19.)
//
// THE RULE: never bind a boolean prop (disabled/loading/checked/open/required/...) directly
// as a JSX attribute on a <wa-*> element. Route it through this hook or a wa/ wrapper
// (WaButton, WaSwitch), which set it as a *property* on the element instead of an attribute.
// Lit reads the property correctly, and re-applies pre-upgrade own-properties on upgrade, so
// property-before-upgrade is safe (proven by WaSwitch in production).
//
// useLayoutEffect (not useEffect) so the property is set before paint — no one-frame flash
// of the wrong state on mount.
//
// useWaBooleanProps(ref, { disabled, loading }) — pass a ref to the wa-* element and an
// object of boolean-valued props. Each is coerced to a strict boolean and set as a property.
export default function useWaBooleanProps(ref, boolProps) {
	const keys = Object.keys(boolProps);
	const values = keys.map((k) => boolProps[k]);

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return;
		for (const key of keys) {
			el[key] = !!boolProps[key];
		}
		// keys/values are spread into the dep list below; boolProps is a fresh object each
		// render so we intentionally key on the individual values, not the object identity.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ref, ...values]);
}
